import { Button } from "@/components/flexkit/Button";
import { Nudge } from "@/components/nudges/Nudge";
import { Text } from "@/components/podkit/typography/Text";

import { NudgeKeys, useNudges } from "@/hooks/use-nudges";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useCallback, useEffect, useState, type FC } from "react";
import { TrackLocations } from "@/hooks/use-segment";
import { useHasArchivedEnvironments } from "@/queries/environment-queries";

type ArchivedEnvironmentNudgeProps = {
    setIsArchiveView?: (isArchive: boolean) => void;
};

export const ArchivedEnvironmentNudge: FC<ArchivedEnvironmentNudgeProps> = ({ setIsArchiveView }) => {
    const { active: nudge, error: nudgeError, isPending: isNudgePending } = useNudges();
    const {
        value: archivePreferenceValue,
        setValue: setArchivePreference,
        queryData: { isPending: isPreferencesPending, error: preferencesError },
    } = usePreferenceValue(NudgeKeys.ARCHIVE_FEATURE_INTRODUCED);
    const {
        hasArchivedEnvironments,
        isPending: isEnvironmentsPending,
        error: environmentsError,
    } = useHasArchivedEnvironments();
    const [open, setOpen] = useState(false);

    const handleReview = useCallback(() => {
        setIsArchiveView?.(true);
        setArchivePreference("true");
        setOpen(false);
    }, [setIsArchiveView, setArchivePreference]);

    const handleDismiss = useCallback(() => {
        setOpen((previousOpenState) => {
            const isNowOpen = !previousOpenState;
            if (!isNowOpen) {
                setArchivePreference("true");
            }
            return isNowOpen;
        });
    }, [setArchivePreference]);

    // Auto-show nudge if applicable
    useEffect(() => {
        if (
            isNudgePending ||
            nudgeError ||
            isEnvironmentsPending ||
            environmentsError ||
            isPreferencesPending ||
            preferencesError
        ) {
            return;
        }

        const hasSeenArchiveFeature = archivePreferenceValue === "true";

        if (nudge?.key === NudgeKeys.ARCHIVE_FEATURE_INTRODUCED && !hasSeenArchiveFeature && hasArchivedEnvironments) {
            setOpen(true);
            return;
        }

        setOpen(false);
    }, [
        isNudgePending,
        nudgeError,
        isEnvironmentsPending,
        environmentsError,
        isPreferencesPending,
        preferencesError,
        nudge?.key,
        archivePreferenceValue,
        hasArchivedEnvironments,
    ]);

    const ariaLabelPrefix = "Announcement - Archived Environment";

    return (
        <Nudge
            open={open}
            toggleOpen={handleDismiss}
            ariaLabelPrefix={ariaLabelPrefix}
            showCloseButton={false}
            data-testid="nudge-archived-environment-trigger"
            data-track-location={TrackLocations.ArchivedEnvironmentNudge}
        >
            <div className="flex flex-col gap-2">
                <div className="p-2 text-center">
                    <Text className="text-base font-bold text-content-primary">
                        We&apos;ve archived an environment that wasn&apos;t used in 7 days
                    </Text>
                </div>
                <div className="flex flex-col gap-2">
                    <Button
                        variant="primary"
                        size="md"
                        className="grow"
                        aria-label={`${ariaLabelPrefix} - Review archived environments`}
                        onClick={handleReview}
                        data-tracking-id="review-archived-environments-nudge"
                    >
                        Review
                    </Button>
                    <Button
                        variant="secondary"
                        size="md"
                        className="grow"
                        aria-label={`${ariaLabelPrefix} - Dismiss`}
                        onClick={handleDismiss}
                        data-tracking-id="dismiss-archived-environments-nudge"
                    >
                        Dismiss
                    </Button>
                </div>
            </div>
        </Nudge>
    );
};
